import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import useUserAuth from '../../../utils/useUserAuth';
import ConsultationEdit from './modal/ConsultationEditModal';
import ConsultationDeleteModal from './modal/ConsultationDeleteModal';
import CommonLoading from '../../../form-components/CommonLoading';
import {
  Container,
  HeaderSection,
  WriteButton,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableHeaderText,
  TableBody,
  TableRow,
  TableCell,
  TableCellText,
  ActionButtonGroup,
  EditButton,
  EditButtonText,
  DeleteButton,
  DeleteButtonText,
  EmptyState,
  EmptyStateText,
} from './ConsultationHistoryStyled';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConsultationList, deleteConsultation } from '../../../api/consultation';
import { formatVisitPath } from '../../../constants/visitPath';
import { getConsultMethodLabel, getConsultPurposeLabel } from '../../../constants/consultation';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// 테이블 컬럼 설정
const TABLE_COLUMNS = [
  { key: 'consultDate', label: '상담 날짜', width: '200px' },
  { key: 'title', label: '제목', width: '320px' },
  { key: 'consultMethod', label: '상담 수단', width: '160px' },
  { key: 'consultPurpose', label: '상담 목적', width: '160px' },
  { key: 'visitPath', label: '방문경로', width: '140px' },
  { key: 'managerName', label: '관리 담당자', width: '120px' },
  { key: 'updatedDate', label: '최근 수정일', width: '200px' },
  { key: 'actions', label: '수정 및 삭제', width: '128px' },
];



// consultType: 'MEMBER' | 'TEMPORARY' — 회원/잠재회원 상세에서 공용으로 사용
const ConsultationHistory = ({ member, bigUser, consultType = 'MEMBER' }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canConsultation = useUserAuth('MEMBER_CONSULTATION');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit' | 'detail'
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isTemporary = consultType === 'TEMPORARY';
  const targetId = isTemporary ? member?.temporaryUserId ?? member?.id : member?.memberId;
  const queryScope = isTemporary ? 'temporary' : 'member';
  const listParams = isTemporary ? { temporaryUserId: targetId } : { memberId: targetId };

  const { data: consultationListData = [], isLoading } = useQuery({
    queryKey: ['consultationList', queryScope, targetId],
    queryFn: () => getConsultationList(listParams, user?.token),
    enabled: !!targetId && !!user?.token,
  });

  // 상담 작성
  const handleWrite = () => {
    setModalData(null);
    setModalMode('create');
    setModalOpen(true);
  };

  // 행 클릭 — 그 날짜의 지점 스케줄로 이동해 해당 상담 카드에 포커스한다.
  // 상세 내용 확인·수정은 행의 수정 버튼으로 들어간다.
  const handleRowClick = consultation => {
    const consultationId = consultation.consultationId ?? consultation.id;
    if (!consultation.consultDate || !consultationId) return;
    navigate(`/private?date=${consultation.consultDate}&consultationId=${consultationId}`);
  };

  // 상담 수정 (수정 버튼 클릭 또는 상세보기에서 수정 버튼 클릭)
  const handleEdit = consultation => {
    setModalData(consultation);
    setModalMode('edit');
    setModalOpen(true);
  };

  // 상담 삭제
  const handleDelete = consultation => {
    setDeleteTarget(consultation);
    setDeleteModalOpen(true);
  };

  // 상담 삭제 mutation
  const deleteMutation = useMutation({
    mutationFn: consultationId => deleteConsultation(consultationId, user?.token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultationList', queryScope, targetId] });
      handleDeleteModalClose();
    },
  });

  const handleDeleteConfirm = () => {
    const consultationId = deleteTarget?.consultationId || deleteTarget?.id;
    if (consultationId) {
      deleteMutation.mutate(consultationId);
    }
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalData(null);
    setModalMode('create');
  };

  return (
    <Container>
      {/* 상담작성 버튼 */}
      <HeaderSection>
        <Tooltip title={!canConsultation ? '권한이 없습니다' : ''} arrow>
          <WriteButton
            onClick={canConsultation ? handleWrite : undefined}
            style={!canConsultation ? { opacity: 0.5, cursor: 'default' } : {}}>
            <EditIcon />
            {t('상담작성')}
          </WriteButton>
        </Tooltip>
      </HeaderSection>

      {/* 상담 내역 테이블 */}
      <TableContainer>
        {/* 테이블 헤더 */}
        <TableHeader>
          {TABLE_COLUMNS.map(column => (
            <TableHeaderCell key={column.key} width={column.width}>
              <TableHeaderText>{t(column.label)}</TableHeaderText>
            </TableHeaderCell>
          ))}
        </TableHeader>

        {/* 테이블 바디 — 로딩 중에는 스피너, 그 외에는 데이터/빈 상태. */}
        <TableBody>
          {isLoading ? (
            <CommonLoading minHeight={240} />
          ) : consultationListData.length > 0 ? (
            consultationListData.map(consultation => (
              <TableRow key={consultation.id} onClick={() => handleRowClick(consultation)}>
                <TableCell width="200px">
                  <TableCellText>{consultation.consultDate}</TableCellText>
                </TableCell>
                <TableCell width="320px">
                  <TableCellText>{consultation.title}</TableCellText>
                </TableCell>
                <TableCell width="160px">
                  <TableCellText>
                    {getConsultMethodLabel(consultation.consultMethod)}
                  </TableCellText>
                </TableCell>
                <TableCell width="160px">
                  <TableCellText>
                    {getConsultPurposeLabel(consultation.consultPurpose)}
                  </TableCellText>
                </TableCell>
                <TableCell width="140px">
                  <TableCellText>
                    {formatVisitPath(consultation.visitPath, consultation.visitPathEtc)}
                  </TableCellText>
                </TableCell>
                <TableCell width="120px">
                  <TableCellText>{consultation.managerName}</TableCellText>
                </TableCell>
                <TableCell width="200px">
                  <TableCellText>{consultation.updatedDate?.split(' ')[0]}</TableCellText>
                </TableCell>
                <TableCell width="128px" onClick={e => e.stopPropagation()}>
                  <ActionButtonGroup>
                    <EditButton onClick={() => handleEdit(consultation)}>
                      <EditButtonText>{t('수정')}</EditButtonText>
                    </EditButton>
                    <DeleteButton onClick={() => handleDelete(consultation)}>
                      <DeleteButtonText>{t('삭제')}</DeleteButtonText>
                    </DeleteButton>
                  </ActionButtonGroup>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <EmptyState>
              <EmptyStateText>{t('상담 내역이 없습니다.')}</EmptyStateText>
            </EmptyState>
          )}
        </TableBody>
      </TableContainer>

      {/* 상담 작성/수정/상세 모달 */}
      <ConsultationEdit
        open={modalOpen}
        onClose={handleModalClose}
        editData={modalData}
        mode={modalMode}
        onEdit={handleEdit}
        consultType={consultType}
        targetId={targetId}
        memberId={targetId}
      />

      {/* 상담 삭제 확인 모달 */}
      <ConsultationDeleteModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
      />
    </Container>
  );
};

export default ConsultationHistory;
